import React, { useState, useEffect, useMemo } from 'react';
import { FormControl, InputLabel, MenuItem, Select, TextField, makeStyles } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { wsGet, wsFindBy, wsGetAll, wsPost, wsRemove, wsUpdate } from 'services/WService';
import CardContainer from 'components/Tvendo/Common/CardContainer';
import GridContainer from 'components/Grid/GridContainer';
import ReactTable from 'components/ReactTable/ReactTable';
import Button from 'components/CustomButtons/Button';
import SweetAlert from 'react-bootstrap-sweetalert';
import { Add, Delete } from '@material-ui/icons';
import GridItem from 'components/Grid/GridItem';
import { useParams } from 'react-router-dom';
import styles from 'assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.js';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';
import MultiComplete from 'components/Tvendo/Common/MultiComplete';
import Badge from 'components/Badge/Badge';
import { AllowedGuard } from 'components/Tvendo/Common/UserAction';
import { ConstantPermissions } from 'contexts/useConstant';
import { useAuth } from 'hooks/useAuth';

const columns = [
  { Header: '#', accessor: 'num', excelEnabled: false },
  { Header: 'Código', accessor: 'item_code' },
  { Header: 'Descripción', accessor: 'item_description' },
  { Header: '', accessor: 'actions', excelEnabled: false, width: 0 },
];

const columns2 = [
  { Header: '#', accessor: 'num', excelEnabled: false },
  { Header: 'Código', accessor: 'item_code', width: 150 },
  { Header: 'Descripción', accessor: 'item_description' },
  { Header: '', accessor: 'actions', excelEnabled: false, width: 0 },
];

const useStyles = makeStyles(() => ({
  ...styles,
}));

function DiscountRuleDetail(props) {
  const classes = useStyles();
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const canUpdate = hasPermission(ConstantPermissions.LOGI_RULE_DISCOUNTS_UPDATE);

  const [discountRule, setDiscountRule] = useState({});
  const [details, setDetails] = useState([]);
  const [items, setItems] = useState([]);
  const [zones, setZones] = useState([]);
  const [bureaus, setBureaus] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);
  const [selectedBureaus, setSelectedBureaus] = useState([]);
  const [alert, setAlert] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);

  const typeDescription = useMemo(() => {
    switch (discountRule.item_type) {
      case 'C':
        return 'Categorías';
      case 'L':
        return 'Líneas';
      case 'F':
        return 'Familias';
      case 'P':
        return 'Productos';
      default:
        return null;
    }
  }, [discountRule.item_type]);

  async function addDetail(itemId) {
    setDataLoading(true);
    await wsPost(`logi/discount-rule/${discountRule.id}/detail`, {
      item_id: itemId,
      excluded: discountRule.item_type === 'P' ? 'Y' : 'N',
    });
    await loadDiscountRule();
  }

  async function deleteDetail(itemId) {
    setDataLoading(true);
    await wsRemove(`logi/discount-rule/${discountRule.id}/detail`, itemId);
    await loadDiscountRule();
  }

  async function loadDiscountRule() {
    setDataLoading(true);
    const res = await wsGet('logi/discount-rule', id);
    setDataLoading(false);
    if (!res || res.success === false || !res.data) {
      return;
    }
    const { details, ...data } = res.data;
    setDiscountRule(data);
    setDetails(details ?? []);
  }

  async function loadZones() {
    let res = await wsGetAll('base/zone');
    if (res.success) setZones(res.data);
  }

  async function loadBureaus() {
    let res = await wsGetAll('base/bureau');
    if (res.success) setBureaus(res.data);
  }

  async function saveDetailType(e) {
    let detail_type = e.target.value;
    setDiscountRule((rule) => ({ ...rule, detail_type }));
    setDataLoading(true);
    await wsUpdate('logi/discount-rule', discountRule.id, { detail_type });
    setDataLoading(false);
  }

  function saveItemType(e) {
    let item_type = e.target.value;
    setAlert(
      <SweetAlert
        warning
        style={{ display: 'block', marginTop: '-100px' }}
        title="Cambio de tipo detalles"
        onConfirm={async () => {
          setDataLoading(true);
          let res = await wsUpdate('logi/discount-rule', discountRule.id, {
            item_type,
          });
          setDataLoading(false);
          if (res.success) {
            setDiscountRule((rule) => ({ ...rule, item_type }));
            setDetails([]);
            setAlert(null);
          }
        }}
        onCancel={() => setAlert(null)}
        confirmBtnCssClass={classes.button + ' ' + classes.success}
        cancelBtnCssClass={classes.button + ' ' + classes.danger}
        confirmBtnText="Aceptar"
        cancelBtnText="Cancelar"
        showCancel
      >
        ¡Esto eliminará todos los detalles!
      </SweetAlert>,
    );
  }

  // Solo zonas de almacen (code === defCashier)
  const warehouseZones = useMemo(
    () => zones.filter((z) => z.code && z.defCashier && z.code === z.defCashier),
    [zones],
  );

  const selectedZoneIds = useMemo(
    () => selectedZones.map((z) => String(z.id)),
    [selectedZones],
  );

  const filteredBureaus = useMemo(() => {
    if (selectedZoneIds.length === 0) return [];
    return bureaus.filter((b) => selectedZoneIds.includes(String(b.base_zone_id)));
  }, [bureaus, selectedZoneIds]);

  async function onZonesChange(items) {
    setSelectedZones(items);
    const base_zone_ids = (items || []).map((z) => z.id);
    const zoneIds = base_zone_ids.map(String);

    const keptBureaus = selectedBureaus.filter((b) => zoneIds.includes(String(b.base_zone_id)));
    const droppedSome = keptBureaus.length !== selectedBureaus.length;

    setDataLoading(true);
    await wsUpdate('logi/discount-rule', discountRule.id, { base_zone_ids });
    if (droppedSome) {
      setSelectedBureaus(keptBureaus);
      await wsUpdate('logi/discount-rule', discountRule.id, {
        base_bureau_ids: keptBureaus.map((b) => b.id),
      });
    }
    setDataLoading(false);
  }

  async function onBureausChange(items) {
    setSelectedBureaus(items);
    const base_bureau_ids = (items || []).map((b) => b.id);
    setDataLoading(true);
    await wsUpdate('logi/discount-rule', discountRule.id, { base_bureau_ids });
    setDataLoading(false);
  }

  async function saveQuantity(field, e) {
    let raw = e.target.value;
    let value = raw === '' ? null : Number(raw);
    setDiscountRule((rule) => ({ ...rule, [field]: value }));
    setDataLoading(true);
    await wsUpdate('logi/discount-rule', discountRule.id, { [field]: value });
    setDataLoading(false);
  }

  useEffect(() => {
    loadDiscountRule();
    loadZones();
    loadBureaus();
  }, []);

  useEffect(() => {
    loadItems();
  }, [discountRule.item_type]);

  useEffect(() => {
    if (warehouseZones.length > 0 && Array.isArray(discountRule.base_zone_ids)) {
      const ids = discountRule.base_zone_ids.map(String);
      setSelectedZones(warehouseZones.filter((z) => ids.includes(String(z.id))));
    }
  }, [warehouseZones, discountRule.base_zone_ids]);

  useEffect(() => {
    if (bureaus.length > 0 && Array.isArray(discountRule.base_bureau_ids)) {
      const ids = discountRule.base_bureau_ids.map(String);
      setSelectedBureaus(bureaus.filter((b) => ids.includes(String(b.id))));
    }
  }, [bureaus, discountRule.base_bureau_ids]);

  async function loadItems() {
    if (discountRule.id === undefined) return;
    let search = {};
    for (let filterKey of [
      'base_business_partner_id',
      'logi_mark_id',
      'logi_category_id',
      'logi_line_id',
      'logi_family_id',
    ]) {
      if (discountRule[filterKey] !== null && discountRule[filterKey] !== undefined) {
        search[filterKey] = discountRule[filterKey];
      }
    }
    let res = null;
    switch (discountRule.item_type) {
      case 'C':
        res = await wsFindBy('logi/category', search);
        setItems(
          res.data.map((item) => ({
            id: item.id,
            item_id: item.id,
            item_code: item.code,
            item_description: item.description,
          })),
        );
        break;
      case 'L':
        res = await wsFindBy('logi/line', search);
        setItems(
          res.data.map((item) => ({
            id: item.id,
            item_id: item.id,
            item_code: item.code,
            item_description: item.description,
          })),
        );
        break;
      case 'F':
        res = await wsFindBy('logi/family', search);
        setItems(
          res.data.map((item) => ({
            id: item.id,
            item_id: item.id,
            item_code: item.code,
            item_description: item.description,
          })),
        );
        break;
      case 'P':
        res = await wsFindBy('logi/product', search);
        setItems(
          res.data.map((item) => ({
            id: item.id,
            item_id: item.id,
            item_code: item.ItemCode,
            item_description: item.ItemName,
          })),
        );
        break;
    }
  }

  const noAssignedBureauBag = (
    <div className="actions-center">
      <Badge color="info">Todas las mesas</Badge>
    </div>
  );

  const noAssignedBag = (
    <div className="actions-center">
      <Badge color="info">No asignado</Badge>
    </div>
  );

  return (
    <CardContainer
      title={
        <>
          Detalle de regla de descuento {dataLoading && <LoadingSpinner />}
        </>
      }
    >
      {alert}
      {/* Cabecera */}
      <GridItem xs={12}>
        <GridContainer>
          <GridItem xs={1}>
            <h6>id</h6>
            <h5>{discountRule.id}</h5>
          </GridItem>
          <GridItem xs={3}>
            <h6>Mesas</h6>
            <h5>
              {discountRule.base_bureau_descriptions
                ? discountRule.base_bureau_descriptions
                : noAssignedBureauBag}
            </h5>
          </GridItem>
          <GridItem xs={3}>
            <h6>Marca</h6>
            <h5>
              {discountRule.logi_mark_description
                ? `(${discountRule.logi_mark_code}) ${discountRule.logi_mark_description}`
                : noAssignedBag}
            </h5>
          </GridItem>
          <GridItem xs={5}>
            <h6>Proveedor</h6>
            <h5>
              ({discountRule.CardCode}) {discountRule.CardName}
            </h5>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={4}>
            <h6>Categoría</h6>
            <h5>{discountRule.logi_category_description || noAssignedBag}</h5>
          </GridItem>
          <GridItem xs={4}>
            <h6>Línea</h6>
            <h5>{discountRule.logi_line_description || noAssignedBag}</h5>
          </GridItem>
          <GridItem xs={4}>
            <h6>Familia</h6>
            <h5>{discountRule.logi_family_description || noAssignedBag}</h5>
          </GridItem>

          <GridItem xs={4}>
            <MultiComplete
              label="Zonas"
              selectAll
              limitTags={3}
              textField="description"
              options={warehouseZones}
              selected={selectedZones}
              setSelected={onZonesChange}
              disabled={!canUpdate}
            />
          </GridItem>

          <GridItem xs={4}>
            <MultiComplete
              label={selectedZones.length > 0 ? 'Mesas' : 'Mesas'}
              selectAll
              limitTags={3}
              textField="description"
              options={filteredBureaus}
              selected={selectedBureaus}
              setSelected={onBureausChange}
              disabled={selectedZones.length === 0 || !canUpdate}
            />
          </GridItem>
          {/* item_type */}
          <GridItem xs={4}>
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>Tipo detalle</InputLabel>
              <Select value={discountRule.item_type || 'P'} onChange={saveItemType} disabled={!canUpdate}>
                <MenuItem value="C">Categoría</MenuItem>
                <MenuItem value="L">Línea</MenuItem>
                <MenuItem value="F">Família</MenuItem>
                <MenuItem value="P">Producto</MenuItem>
              </Select>
            </FormControl>
          </GridItem>
          <GridItem xs={4}>
            <TextField
              label="Cantidad mín (cajas)"
              type="number"
              fullWidth
              margin="normal"
              size="small"
              value={discountRule.quantity_min ?? ''}
              onChange={(e) =>
                setDiscountRule((rule) => ({
                  ...rule,
                  quantity_min: e.target.value === '' ? null : Number(e.target.value),
                }))
              }
              onBlur={(e) => saveQuantity('quantity_min', e)}
              inputProps={{ min: 0, step: 1 }}
              disabled={!canUpdate}
            />
          </GridItem>

          <GridItem xs={4}>
            <TextField
              label="Cantidad máx (cajas)"
              type="number"
              fullWidth
              margin="normal"
              size="small"
              value={discountRule.quantity_max ?? ''}
              onChange={(e) =>
                setDiscountRule((rule) => ({
                  ...rule,
                  quantity_max: e.target.value === '' ? null : Number(e.target.value),
                }))
              }
              onBlur={(e) => saveQuantity('quantity_max', e)}
              inputProps={{ min: 0, step: 1 }}
              disabled={!canUpdate}
            />
          </GridItem>

          {/* detail_type */}
          <GridItem xs={4}>
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>Funcionalidad de detalle</InputLabel>
              <Select
                value={discountRule.detail_type || 'E'}
                inputProps={{ name: 'detail_type' }}
                onChange={saveDetailType}
                disabled={!canUpdate}
              >
                <MenuItem value="E">Excluir</MenuItem>
                <MenuItem value="I">Incluir</MenuItem>
              </Select>
            </FormControl>
          </GridItem>

        </GridContainer>
      </GridItem>

      {/* Tablas */}
      <GridItem xs={12}>
        <h3>
          {typeDescription === null
            ? 'Cargando...'
            : `${typeDescription} ${discountRule.detail_type === 'E' ? 'excluidos' : 'incluidos'}`}
        </h3>
        <ReactTable
          columns={columns}
          data={details.map((item, index) => {
            return {
              num: (
                <div className="actions-right">
                  {index + 1}
                  <AllowedGuard permission={ConstantPermissions.LOGI_RULE_DISCOUNTS_UPDATE}>
                    <Button
                      justIcon
                      round
                      simple
                      color="danger"
                      onClick={() => deleteDetail(item.item_id)}
                    >
                      <Delete />
                    </Button>
                  </AllowedGuard>
                </div>
              ),
              ...item,
            };
          })}
        />
      </GridItem>
      <GridItem xs={12}>
        <h3>
          {typeDescription === null
            ? 'Cargando...'
            : `${typeDescription} a ${discountRule.detail_type === 'E' ? 'excluir' : 'incluir'}`}
        </h3>
        <ReactTable
          columns={columns2}
          data={items
            .filter((item) => {
              let index = details.findIndex((detail) => detail.item_id == item.id);
              if (index !== -1) return false;
              return true;
            })
            .map((item, index) => {
              return {
                num: (
                  <div className="actions-right">
                    {index + 1}
                    <AllowedGuard permission={ConstantPermissions.LOGI_RULE_DISCOUNTS_UPDATE}>
                      <Button
                        justIcon
                        round
                        simple
                        color="success"
                        onClick={() => addDetail(item.id)}
                      >
                        <Add />
                      </Button>
                    </AllowedGuard>
                  </div>
                ),
                ...item,
              };
            })}
        />
      </GridItem>
    </CardContainer>
  );
}

export default DiscountRuleDetail;
